/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AssignableCourseResult } from '../models/AssignableCourseResult';
import type { ComplianceConfigurationDTO } from '../models/ComplianceConfigurationDTO';
import type { Course } from '../models/Course';
import type { CourseEntity } from '../models/CourseEntity';
import type { CourseHeartbeat } from '../models/CourseHeartbeat';
import type { CourseReportDTO } from '../models/CourseReportDTO';
import type { UsersToCourseRequest } from '../models/UsersToCourseRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class CoursesService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List All Courses
     * List All Courses
     * @returns CourseEntity OK
     * @throws ApiError
     */
    public getAllCourses({
        isAdmin = false,
        type = '',
    }: {
        isAdmin?: boolean,
        type?: string,
    }): CancelablePromise<Array<CourseEntity>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/courses',
            query: {
                'isAdmin': isAdmin,
                'type': type,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Add a Course
     * @returns CourseEntity OK
     * @throws ApiError
     */
    public addCourse({
        requestBody,
    }: {
        requestBody: CourseEntity,
    }): CancelablePromise<CourseEntity> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/courses',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Assign one or more Team members to a Checklist
     * @returns any Created
     * @throws ApiError
     */
    public assignUsersToChecklist({
        courseId,
        requestBody,
    }: {
        /**
         * ID of course to set the status.
         */
        courseId: string,
        requestBody: UsersToCourseRequest,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/courses/assign/{pathwayCourseId}',
            path: {
                'courseId': courseId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Status of all current enrollments to this course by optional location
     * @returns AssignableCourseResult Ok
     * @throws ApiError
     */
    public listEnrollmentsByCourse({
        courseId,
        listOfLocationsToFilter,
    }: {
        /**
         * ID of course
         */
        courseId: string,
        listOfLocationsToFilter?: string,
    }): CancelablePromise<AssignableCourseResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/courses/assigned/{courseId}',
            path: {
                'courseId': courseId,
            },
            query: {
                'List of locations to filter': listOfLocationsToFilter,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Gets compliance courses based on country selected in user's profile
     * @returns Course Ok
     * @throws ApiError
     */
    public getComplianceCourses(): CancelablePromise<Array<Course>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/courses/compliance',
            errors: {
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Sensitive data report on course by location
     * @returns CourseReportDTO Ok
     * @throws ApiError
     */
    public generateCourseReport({
        courseId,
        listOfLocationsToFilter,
    }: {
        /**
         * ID of course
         */
        courseId: string,
        listOfLocationsToFilter: string,
    }): CancelablePromise<CourseReportDTO> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/courses/report/{courseId}',
            path: {
                'courseId': courseId,
            },
            query: {
                'List of locations to filter': listOfLocationsToFilter,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Returns course settings for an operator
     * @returns ComplianceConfigurationDTO Updated
     * @throws ApiError
     */
    public getOperatorCourseSettings({
        operatorId,
    }: {
        /**
         * The ID of the operator setting to modify
         */
        operatorId: string,
    }): CancelablePromise<Record<string, ComplianceConfigurationDTO>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/courses/settings',
            query: {
                'operatorID': operatorId,
            },
            errors: {
                400: `Error`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Enables or Disables a course for an operator
     * @returns any Updated
     * @throws ApiError
     */
    public setOperatorCourseSetting({
        courseId,
        enable,
        operatorId,
    }: {
        /**
         * ID of the course
         */
        courseId: string,
        /**
         * Set to either enable/disable the course
         */
        enable: string,
        /**
         * The ID of the operator setting to modify
         */
        operatorId: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/courses/settings/{courseId}/{enable}',
            path: {
                'courseId': courseId,
                'enable': enable,
            },
            query: {
                'operatorID': operatorId,
            },
            errors: {
                400: `Error`,
                403: `Forbidden`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Get SQSSO
     * @returns string OK
     * @throws ApiError
     */
    public getSqssl({
        courseId,
        lang,
    }: {
        /**
         * ID of course.
         */
        courseId: string,
        lang?: string,
    }): CancelablePromise<Record<string, string>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/courses/sqsso/{courseId}',
            path: {
                'courseId': courseId,
            },
            query: {
                'lang': lang,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Synchronize User with LearnUpon
     * @returns any OK
     * @throws ApiError
     */
    public synchronizeUser({
        userId,
    }: {
        /**
         * ID of user
         */
        userId: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/courses/sync/{userId}',
            path: {
                'userId': userId,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Handle LearnUpon webhook event
     * @returns void
     * @throws ApiError
     */
    public learnUponWebhook({
        requestBody,
    }: {
        requestBody: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/courses/webhook',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
            },
        });
    }

    /**
     * Delete Course by ID
     * @returns any Accepted
     * @throws ApiError
     */
    public deleteCourse({
        id,
    }: {
        /**
         * ID of Course to Delete.
         */
        id: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/courses/{id}',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Get Course by ID
     * @returns CourseEntity OK
     * @throws ApiError
     */
    public getCourseById({
        id,
    }: {
        /**
         * ID of Course to Get.
         */
        id: string,
    }): CancelablePromise<CourseEntity> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/courses/{id}',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Update a course
     * @returns CourseEntity OK
     * @throws ApiError
     */
    public updateCourse({
        id,
        requestBody,
    }: {
        /**
         * ID of Course to Update.
         */
        id: string,
        requestBody: CourseEntity,
    }): CancelablePromise<CourseEntity> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/courses/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Set course status by ID
     * @returns any Ok
     * @throws ApiError
     */
    public updateUserComplianceCourseDuration({
        pathwayCourseId,
        requestBody,
    }: {
        /**
         * ID of course.
         */
        pathwayCourseId: string,
        requestBody?: CourseHeartbeat,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/courses/{pathwayCourseId}/heartbeat',
            path: {
                'pathwayCourseId': pathwayCourseId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                500: `Internal Server Error`,
            },
        });
    }

}
